import { useCallback } from 'react';

import { ArrowUpward } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import { themeColors } from 'colors';

export type Props = {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    disabled: boolean;
};

const ChatComposer = ({ value, onChange, onSend, disabled }: Props) => {
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey && !disabled) {
                event.preventDefault();
                onSend();
            }
        },
        [onSend, disabled]
    );

    return (
        <Box
            role="region"
            aria-label="Message composer"
            data-testid="copy-generator-chat-composer"
            sx={{
                flexShrink: 0,
                px: 2,
                pb: 1,
                pt: 2,
                background:
                    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 30%, rgb(255,255,255) 100%)',
            }}
        >
            <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder="Ask Sara to help you write"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                variant="outlined"
                size="small"
                inputProps={{
                    'aria-label': 'Message to Ask Sara',
                    'data-testid': 'copy-generator-chat-composer-input',
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 2.2, ml: 0, mr: 0.5 }}>
                            <IconButton
                                type="button"
                                aria-label="Send message"
                                data-testid="copy-generator-chat-send"
                                disabled={disabled}
                                onClick={onSend}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0,
                                    borderRadius: '6px',
                                    color: themeColors.primary.contrastText,
                                    bgcolor: 'action.active',
                                    '&:not(.Mui-disabled):hover': {
                                        bgcolor: 'action.selected',
                                    },
                                    '&.Mui-disabled': {
                                        color: themeColors.primary.contrastText,
                                        bgcolor: 'action.disabled',
                                        opacity: 1,
                                    },
                                }}
                            >
                                <ArrowUpward sx={{ fontSize: 18 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: 48,
                        alignItems: 'flex-start',
                        py: 0.75,
                        pr: 0.5,
                        borderRadius: '6px',
                        bgcolor: 'background.paper',
                        boxShadow: `1px 1px 0px 0px ${themeColors.primary.main}`,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: themeColors.text.primary,
                        fontSize: 14,
                        letterSpacing: '0.15px',
                        lineHeight: '20px',
                        py: 0.75,
                        '&::placeholder': {
                            color: themeColors.text.disabled,
                            opacity: 1,
                        },
                    },
                }}
            />
            <Typography
                color="text.secondary"
                sx={{ mt: 1, textAlign: 'center', fontSize: 12, letterSpacing: '0.4px' }}
                variant="caption"
            >
                AI can make mistakes. Review before sending. Learn more
            </Typography>
        </Box>
    );
};

export default ChatComposer;
